// import * as Sentry from '@sentry/browser';
import { Analytics, getAnalytics, isSupported } from 'firebase/analytics';
import { initializeApp } from 'firebase/app';
import { initializeAppCheck, ReCaptchaV3Provider } from 'firebase/app-check';
import { getAuth } from 'firebase/auth';
import { getDatabase } from 'firebase/database';
import { initializeFirestore } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { FirebasePerformance, getPerformance } from 'firebase/performance';
import { getStorage } from 'firebase/storage';
import { logError } from './logError';

const clientCredentials = {
  apiKey: process.env.NEXT_PUBLIC_FIREBASE_API_KEY,
  authDomain: process.env.NEXT_PUBLIC_FIREBASE_AUTH_DOMAIN,
  databaseURL: process.env.NEXT_PUBLIC_FIREBASE_DATABASE_URL,
  projectId: process.env.NEXT_PUBLIC_FIREBASE_PROJECT_ID,
  storageBucket: process.env.NEXT_PUBLIC_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.NEXT_PUBLIC_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.NEXT_PUBLIC_FIREBASE_APP_ID,
  measurementId: process.env.NEXT_PUBLIC_FIREBASE_MEASUREMENT_ID,
};

const firebaseApp = initializeApp(clientCredentials);
const db = initializeFirestore(firebaseApp, {
  ignoreUndefinedProperties: process.env.NODE_ENV === 'production',
  experimentalAutoDetectLongPolling: true,
});
const AUTH = getAuth(firebaseApp);
const functions = getFunctions(firebaseApp);
const storage = getStorage(firebaseApp);
const realtimeDatabase = getDatabase(firebaseApp);
let analyticsInstance: Analytics | null = null;
let analyticsInitPromise: Promise<Analytics | null> | null = null;

async function analyticsPromise(enable = false): Promise<Analytics | null> {
  try {
    if (analyticsInstance) return analyticsInstance;

    if (!enable) return null;

    if (!analyticsInitPromise) {
      analyticsInitPromise = (async () => {
        const supported = await isSupported();
        if (!supported) return null;

        analyticsInstance = getAnalytics(firebaseApp);
        return analyticsInstance;
      })();
    }

    return analyticsInitPromise;
  } catch (e) {
    console.error('Error initializing analytics', e);
    return null;
  }
}

let perf: FirebasePerformance | null = null;

let clientInitialized = false;

export function initializeFirebaseClient() {
  if (typeof window === 'undefined') return;
  if (clientInitialized) return;

  clientInitialized = true;

  if (process.env.NODE_ENV === 'development') {
    (globalThis as any).FIREBASE_APPCHECK_DEBUG_TOKEN = true;
  }

  try {
    perf = getPerformance(firebaseApp);
  } catch (error) {
    logError(error, undefined, undefined, 'Error initializing performance');
  }

  if (process.env.NEXT_PUBLIC_CAPTCHA_SITE_KEY) {
    try {
      initializeAppCheck(firebaseApp, {
        provider: new ReCaptchaV3Provider(
          process.env.NEXT_PUBLIC_CAPTCHA_SITE_KEY
        ),
        isTokenAutoRefreshEnabled: true,
      });
    } catch (error) {
      logError(error, undefined, undefined, 'Error initializing App Check');
    }
  }
}

// export const EMULATORS_STARTED = 'EMULATORS_STARTED';
// function startEmulators() {
//   if (!(global as any)[EMULATORS_STARTED]) {
// (global as any)[EMULATORS_STARTED] = true;
// connectAuthEmulator(AUTH, 'http://localhost:9099');
// connectFunctionsEmulator(functions, 'localhost', 5001);
// connectFirestoreEmulator(db, 'localhost', 8080);
//   }
// }
enum FirebaseCallable {
  banUser = 'v2_banUser',
  acceptOffer = 'v2_acceptOffer',
  addSellerPayoutMethod = 'v2_addSellerPayoutMethod',
  addToCart = 'v2_addToCart',
  addToFlashDeals = 'v2_addToFlashDeals',
  calculateMetrics = 'v2_calculateMetrics',
  chargeAccount = 'v2_chargeAccount',
  chargeUnpaidOrder = 'v2_chargeUnpaidOrder',
  checkRaEligibility = 'v2_checkRAEligibility',
  clearNotifications = 'v2_clearNotifications',
  completeRegistration = 'v2_completeRegistration',
  contactUs = 'v2_contactUs',
  createAndChargeLabel = 'v2_createAndChargeLabel',
  createChat = 'v2_createChat',
  createCustomer = 'v2_createCustomer',
  createOfferIntent = 'v2_createOfferIntent',
  createOrderByCharge = 'v2_createOrderByCharge',
  createPaymentIntent = 'v2_createPaymentIntent',
  createPaymentIntentForLabel = 'v2_createPaymentIntentForLabel',
  createPaymentRequestIntent = 'v2_createPaymentRequestIntent',
  createSeller = 'v2_createSeller',
  createSellerPromoCode = 'v2_createSellerPromoCode',
  createSetupIntent = 'v2_createSetupIntent',
  createTransfer = 'v2_createTransfer',
  createTransferOrder = 'v2_createTransferOrder',
  deleteCard = 'v2_deleteCard',
  deleteExternalAccount = 'v2_deleteExternalAccount',
  exportOrders = 'v2_exportOrderHistory',
  exportPayouts = 'v2_exportPayouts',
  fixLabel = 'v2_fixLabel',
  getBalance = 'v2_getBalance',
  getBankToken = 'v2_getBankToken',
  getBikeGraph = 'v2_getBikeGraph',
  getCustomer = 'v2_getCustomer',
  getDuplicateAccountOrderTable = 'v2_getDuplicateAccountOrderTable',
  getEstimates = 'v2_getEstimates',
  getExternalAccounts = 'v2_getExternalAccounts',
  getLabel = 'v2_getLabel',
  getPaymentInfo = 'v2_getPaymentInfo',
  getPaymentMethods = 'v2_getPaymentMethods',
  getPayoutInfo = 'v2_getPayoutInfo',
  getSellerPayoutSchedules = 'v2_getSellerPayoutSchedules',
  getPromos = 'v2_getPromos',
  getRates = 'v2_getRates',
  getRecommendProducts = 'v2_getShapedProducts',
  // getReturnLabel = 'v2_getReturnLabel',
  getSeelQuote = 'v2_getSeelQuote',
  getSeller = 'v2_getSeller',
  getShopOverview = 'v2_getShopOverview',
  getTax = 'v2_getTax',
  getUserByPhone = 'v2_getUserByPhone',
  getWarehouseVariations = 'integrations-warehouse-v2_getWarehouseVariations',
  resyncWarehouseProduct = 'integrations-warehouse-v2_resyncWarehouseProduct',
  createOrUpdateWarehouseProducts = 'integrations-warehouse-v2_createOrUpdateProducts',
  googleAutocomplete = 'v2_googleAutocomplete',
  impersonateUser = 'v2_impersonateUser',
  initiateCheckout = 'v2_initiateCheckout',
  initiateReturn = 'v2_initiateReturn',
  isSubscribed = 'v2_isSubscribed',
  listTransactions = 'v2_listTransactions',
  makeDefaultExternalAccount = 'v2_makeDefaultExternalAccount',
  markAsReceived = 'v2_markAsReceived',
  markSellerListingsAsDraft = 'v2_markSellerListingsAsDraft',
  payoutSeller = 'v2_payoutSeller',
  payWithBalance = 'v2_payWithBalance',
  postProductComment = 'v2_postProductComment',
  refundOrder = 'v2_refundOrder',
  removeExternalAccount = 'v2_removeExternalAccount',
  retrieveSellerPromoCodes = 'v2_retrieveSellerPromoCodes',
  revokeAppleToken = 'v2_revokeAppleToken',
  saveCard = 'v2_saveCard',
  setPopularity = 'v2_setPopularity',
  setStoreOnSale = 'v2_setStoreOnSale',
  startTracking = 'v2_startTracking',
  subscribeToList = 'v2_subscribeToList',
  toggleSellerPromoCode = 'v2_toggleSellerPromoCode',
  trackPackage = 'v2_trackPackage',
  trackPackageGlobal = 'v2_trackPackageGlobal',
  trackUserInteraction = 'v2_trackUserInteraction',
  updateAuthUser = 'v2_updateAuthUser',
  updateOfferIntent = 'v2_updateOfferIntent',
  updatePaymentIntent = 'v2_updatePaymentIntent',
  updatePaymentIntentMethod = 'v2_updatePaymentIntentMethod',
  updateSellerType = 'v2_updateSellerType',
  updateStripeAccount = 'v2_updateStripeAccount',
  vacationMode = 'v2_vacationMode',
  validateAddress = 'v2_validateAddress',
  viewedProduct = 'v2_viewedProduct',
  voidLabel = 'v2_voidLabel',
  // u ship
  createListing = 'uship-v2_createListing',
  calculateBikeShipping = 'uship-v2_calculateBikeShipping',
  getListing = 'uship-v2_getListing',
  getTracking = 'uship-v2_getTracking',
  // listing tool
  listingToolGetListing = 'listingTool-v2_getListing',
  listingToolFetchMotosport = 'listingTool-v2_fetchMotosport',
  logSmartListingEvent = 'listingTool-v2_logSmartListingEvent',
  // turn14
  turn14EnqueueBrand = 'integrations-turn14-v2_enqueueBrand',
  // shiprush
  setShipRushIntegration = 'integrations-shiprush-v2_setShipRushIntegration',
  removeShipRushIntegration = 'integrations-shiprush-v2_removeShipRushIntegration',
  // shipstation
  setShipStationIntegration = 'integrations-shipstation-v2_setShipStationIntegration',
  removeShipStationIntegration = 'integrations-shipstation-v2_removeShipStationIntegration',
  // shipworks
  setShipWorksIntegration = 'integrations-shipworks-v2_setShipWorksIntegration',
  removeShipWorksIntegration = 'integrations-shipworks-v2_removeShipWorksIntegration',
  // support agent
  getNextMessage = 'support-v2_getNextMessage',
  rewordMessage = 'support-v2_rewordMessage',
  approveMessage = 'support-v2_approveMessage',
  getApprovals = 'support-v2_getApprovals',
  getApprovalTimeSeries = 'support-v2_getApprovalTimeSeries',
  executeAction = 'support-v2_executeAction',
  getGlobalSettings = 'support-v2_getGlobalSettings',
  updateGlobalSettings = 'support-v2_updateGlobalSettings',
  getTaskSettings = 'support-v2_getTaskSettings',
  updateTaskSettings = 'support-v2_updateTaskSettings',
  getDelaySettings = 'support-v2_getDelaySettings',
  updateDelaySettings = 'support-v2_updateDelaySettings',
  getLinearIssue = 'support-v2_getLinearIssue',
  createLinearIssue = 'support-v2_createLinearIssue',
  // faq management
  getFAQs = 'support-v2_getFAQs',
  createFAQ = 'support-v2_createFAQ',
  updateFAQ = 'support-v2_updateFAQ',
  deleteFAQ = 'support-v2_deleteFAQ',
  // ai requests
  enhanceListing = 'ai-requests-v2_enhanceListing',
  // ai product pipeline
  startProductPipelineRun = 'ai-productPipelineOncalls-startProductPipelineRun',
  rerunManualProductPipelineGrouping = 'ai-productPipelineOncalls-rerunManualProductPipelineGrouping',
  applyManualProductPipelineGrouping = 'ai-productPipelineOncalls-applyManualProductPipelineGrouping',
  // typesense
  resyncTypesenseListings = 'v2_resyncTypesenseListings',
}

export {
  analyticsPromise,
  AUTH,
  db,
  FirebaseCallable,
  functions,
  httpsCallable,
  perf,
  realtimeDatabase,
  storage,
};
